import React, { useCallback, useEffect, useMemo, useRef } from "react";

import RUG, { Card, DragArea, DropArea } from "react-upload-gallery";
import { shrinkingRequest } from "./GalleryUpload/uploadQueue.js";
import "react-upload-gallery/dist/style.css";
import "./GalleryUpload/style.css";

import axios from "axios";
import { useAlert } from "react-alert";
import { getIn } from "formik";
import { getFileUrl, getApiUrl } from "../../core/config/api.js";

// Tolerant id-list compare (number/string mismatches are the same image).
const sameIds = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => String(v) === String(b[i]));

const GalleryUpload = ({ field, form, value, onChange, rules, send, reflectOrder = false, slotLabels = null }) => {
  const alert = useAlert();
  const wrapperRef = useRef(null);

  const urlFromResponse = (response) => {
    return getFileUrl(response.id);
  };

  // Mirror the displayed `value` into the bound field — but ONLY when the ids
  // genuinely differ. `value` can change identity every render (a parent's
  // `getIn(...) || []` fallback yields a fresh array when the field is empty);
  // an unconditional setFieldValue here then re-renders → new `value` → fires
  // again → infinite loop ("Maximum update depth exceeded"), freezing/crashing
  // the page. The equality guard makes a no-op write impossible, so identity
  // churn can never drive the loop regardless of what the caller passes.
  useEffect(() => {
    if (!form) return;
    const cur = getIn(form.values, field.name);
    const curIds = Array.isArray(cur) ? cur : [];
    const nextIds = Array.isArray(value) ? value : [];
    if (!sameIds(curIds, nextIds)) {
      form.setFieldValue(field.name, value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Display source: the explicit `value` (what the parent wants shown) takes
  // precedence over the bound field value. This lets a marketplace tab DISPLAY
  // DAO's images (passed as `value`) while WRITING edits to its own scope
  // (`field.name`), so an untouched tab mirrors DAO live without any copying.
  // Coerce to an array: `??` only guards null/undefined, so a non-array bound
  // value (e.g. Formik seeds the add-product `images` field to `''`) would slip
  // through and crash `ids.map` below — which blanked the whole page.
  const rawIds = value ?? field?.value;
  const ids = Array.isArray(rawIds) ? rawIds : [];

  // RUG (react-upload-gallery 0.2.x) reads `initialState` only at mount and
  // keeps its own internal copy thereafter, so it only picks up external
  // changes when its `key` changes and it remounts. Which key depends on the
  // gallery's role:
  //   • SELF-EDITING (DAO tab, or a touched marketplace tab — it reads and
  //     writes the SAME field): key on SET MEMBERSHIP (sorted ids). A drag
  //     reorder keeps the same set → key stable → NO remount → the drag isn't
  //     interrupted and the new order commits via onChange. Adds/removes
  //     change the set → remount → the new image shows.
  //   • MIRRORING (an untouched tab DISPLAYING DAO — reflectOrder=true): key
  //     on the FULL ORDER, so a DAO reorder/upload changes the key → remount
  //     → the tab reflects DAO's new order/content. These galleries aren't the
  //     one being dragged, so remounting them is harmless.
  const membershipKey = [...ids].sort((a, b) => a - b).join(",");
  const galleryKey = reflectOrder ? ids.join(",") : membershipKey;

  // initialState must reflect what the gallery shows after a remount. RUG only
  // re-reads it when `galleryKey` changes (its remount trigger), so memoize on
  // `galleryKey` — NOT `value`. On a self-editing upload (e.g. add-product,
  // where value=undefined and content lives in field.value) the key changes but
  // `value` does not; keying on `value` left initialState empty on remount, so
  // the just-uploaded preview vanished and onChange([]) wiped the field.
  const initialState = useMemo(() => {
    return ids.map((f) => ({ source: getFileUrl(f) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryKey]);

  // Position labels (e.g. WatchFinder slots). RUG owns its card DOM and gives
  // us no per-card render hook, so we stamp a `data-slot` attribute on each
  // `.rug-card` by display order; CSS paints it as a badge. The label rides
  // the actual tile, so it stays correct through RUG's own flex-wrap layout.
  const applySlotLabels = useCallback(() => {
    const root = wrapperRef.current;
    if (!root || !slotLabels) return;
    const cards = root.querySelectorAll(".rug-items .rug-card");
    cards.forEach((card, i) => {
      card.setAttribute(
        "data-slot",
        i < slotLabels.length ? `${i + 1} · ${slotLabels[i]}` : "Not sent"
      );
    });
  }, [slotLabels]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root || !slotLabels) return;
    applySlotLabels();
    // RUG re-orders / adds / removes its own card nodes on drag, upload and
    // delete — all childList mutations. Re-stamp on any such change so the
    // badges always track the live order without us hooking RUG internals.
    const observer = new MutationObserver(applySlotLabels);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [applySlotLabels, galleryKey]);

  return (
    <div
      ref={wrapperRef}
      className={`gallery-upload${slotLabels ? " gallery-upload--slots" : ""}`}
    >
      <RUG
        key={galleryKey}
        action={getApiUrl("/api/files")}
        send={send}
        customRequest={shrinkingRequest}
        inOrder={true}
        rules={rules}
        source={urlFromResponse}
        initialState={initialState}
        headers={{
          Authorization: axios.defaults.headers.common["Authorization"],
        }}
        onChange={(images) => {
          // Guard: RUG can fire onChange during componentDidMount with cards
          // whose source is null (cards from initialState that haven't fully
          // resolved). Skip those — split() on null throws and crashed the
          // listing edit page.
          if (images.some((i) => !i.source || i.source.split(":")[0] === "blob")) {
            return;
          }

          const imageIds = images.map((i) =>
            parseInt(i.source.split("/").slice(-1)[0])
          );

          setTimeout(() => {
            if (typeof form?.setFieldValue === "function") {
              form.setFieldValue(field.name, imageIds);
            }
            if (onChange) {
              onChange(imageIds);
            }
          }, 0);
        }}
        onClick={(image) => window.open(image.source, "_blank")}
        // Upload failures were previously silent: the card kept its blob
        // preview and nothing saved. Status 0 = the browser saw no CORS
        // header, which is what an over-5 MB body produces.
        onError={({ status }) => {
          alert.show(
            status === 0 || status === 413
              ? "Upload failed. The image may be too large (max 5 MB) or the connection dropped — please try again."
              : "Upload failed. Please remove the image and try again."
          );
        }}
        onWarning={(type) => {
          if (type === 'maxNumber') {
            alert.show("You've reached the maximum number of images allowed.");
          } else if (type === 'fileSize') {
            alert.show("Upload failed. File is too large.");
          } else if (type === 'fileType') {
            alert.show("Upload failed. Only image files are accepted.");
          } else {
            alert.show("Upload failed. Please try again.");
          }
        }}
      />
    </div>
  );
};

export default GalleryUpload;
